import { useState, useEffect, useRef, ReactElement } from "react"
import { Modal } from "react-bootstrap"
import { Trans, useTranslation } from "react-i18next"
import "./Tutorial.scss"
import { AWSelect } from "aw-select"
import { useNavigate } from "react-router-dom"
import awayterLogo from "../../utils/resources/awayter_connect_light_cropped.png"

const bannerImages = ["/banners/banner_iot1.webp", "/banners/banner_iot2.webp", "/banners/banner_iot3.webp"]

const normalizeAssetPath = (src: string) => {
    if (!src) return src
    if (src.startsWith("http") || src.startsWith("data:") || src.startsWith("/")) return src
    return `/${src}`
}

type TutorialProps = {
    showModal: boolean
    setShowModal: (show: boolean) => void
}

const Tutorial = ({ showModal, setShowModal }: TutorialProps) => {
    const { t, i18n } = useTranslation()
    const pages = [Page1, Page2, Page3, Page4]
    const bannerHeight = 200
    const [current, setCurrent] = useState(0)
    const CurrentPage = pages[current]
    const [language, setLanguage] = useState({ label: t("Ingles"), value: "eng" })
    const [options, _setOptions] = useState([
        { label: t("Catalan"), value: "cat" },
        { label: t("Castellano"), value: "es" },
        { label: t("Frances"), value: "fr" },
        { label: t("Ingles"), value: "eng" }
    ])
    const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 1000);

    useEffect(() => {
        bannerImages.forEach((src) => {
            const img = new Image()
            img.src = normalizeAssetPath(src)
            img.decode().catch(() => {})
        })
    }, [])

    useEffect(() => {
        if (showModal) {
            setCurrent(0)
        }
    }, [showModal])

    useEffect(() => {
        i18n.changeLanguage(language.value.toString())
    }, [language])

    return (
        <Modal show={showModal} size="xl" onHide={() => setShowModal(false)} centered contentClassName='border-0 shadow-none' >
            <div>
                <img src={current < bannerImages.length ? normalizeAssetPath(bannerImages[current]) : undefined} alt="" fetchPriority="high" decoding="sync" style={{ width: "100%", height: "200px", objectFit: "cover", position: "absolute", visibility: current === 3 ? "hidden" : "visible" }} />
            </div>
            <div style={{ backgroundColor: "#f8f9fa", borderRadius: "0rem" }} className='tutorial-modal-content d-flex justify-content-between pt-3'>
                <button type='button' className=' custom-close position-absolute end-0 me-3 bg-transparent text-muted border-0' aria-label='Close' onClick={() => setShowModal(false)} style={{ color: "black" }}>
                    <i className="bi bi-x-lg"></i>
                </button>
                <div className='align-items-center d-flex'>
                    <button className='btn-Arrow ms-2' disabled={current == 0} onClick={() => setCurrent((c) => Math.max(0, c - 1))}>
                        <i className='bi bi-chevron-left fs-2 p-2'></i>
                    </button>
                </div>
                <div className='tutorial-content-wrapup p-3 w-75 text-center d-flex flex-column h-100'>
                    {current != 3 ? (
                        <>
                            <PageWrapup
                                title={t(`PopUpPage${current + 1}Title`)}
                                isWideScreen={isWideScreen}
                                topOffset={bannerHeight + (isWideScreen ? 20 : 45)}
                            >
                                <CurrentPage />
                            </PageWrapup>
                        </>
                    ) : (
                        <CurrentPage />
                    )}
                    <div className='mt-auto pb-2'>
                        <div className='d-flex justify-content-center gap-2 mt-auto'>
                            {pages.map((_, index) => (
                                <div
                                    key={index}
                                    onClick={() => setCurrent(index)}
                                    style={{
                                        width: current === index ? "20px" : "8px",
                                        height: "8px",
                                        borderRadius: "4px",
                                        backgroundColor: current === index ? "#6c757d" : "#adb5bd",
                                        cursor: "pointer",
                                        transition: "all 0.3s ease"
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className='align-items-center d-flex'>
                    <button className='btn-Arrow me-2' disabled={current >= pages.length - 1} onClick={() => setCurrent((c) => Math.min(pages.length - 1, c + 1))}>
                        <i className='bi bi-chevron-right fs-2 p-2'></i>
                    </button>
                </div>
                <div className='position-absolute end-0 bottom-0 my-3 mx-4'>
                    <img src={awayterLogo} style={{ width: `${isWideScreen ? '150px' : '100px'}`, height: "auto" }} />
                </div>
                <div className='position-absolute start-0 bottom-0 m-3 ms-4 text-white'>
                    <AWSelect menuPlacement="top" className='dark-language' value={language} dark={false} options={options} onChange={(option) => setLanguage(option)} />
                </div>
            </div>
        </Modal>
    )
}

const PageWrapup = ({ title, children, isWideScreen, topOffset }: { title: string; children: ReactElement; isWideScreen: boolean; topOffset: number }) => {
    return (
        <div className="d-flex flex-column h-100" style={{ marginTop: `${topOffset}px` }}>
            <div className="pb-3">
                <h3 className={`fw-bold mb-3 ${!isWideScreen ? "mt-2" : ""}`}>{title}</h3>
                <div>{children}</div>
            </div>
        </div>
    )
}

const Page1 = () => {
    const { t } = useTranslation()
    return (
        <div className='tutorial-container align-items-center d-flex flex-column justify-content-center' style={{ height: "100%" }}>
            <p> <Trans i18nKey="PopUpPage1Paragraph1" components={{ strong: <strong /> }}></Trans></p>
        </div>
    )
}

const Page2 = () => {
    const { t } = useTranslation()
    return (
        <div className='tutorial-container align-items-center d-flex flex-column justify-content-evenly' style={{ height: "100%" }}>
            <p> <Trans i18nKey="PopUpPage2Paragraph1" components={{ strong: <strong /> }}></Trans></p>
        </div>
    )
}

const Page3 = () => {
    const { t } = useTranslation()
    return (
        <div className='tutorial-container align-items-center d-flex flex-column justify-content-evenly' style={{ height: "100%" }}>
            <p> <Trans i18nKey="PopUpPage3Paragraph1" components={{ strong: <strong /> }}></Trans></p>
        </div>
    )
}

const Page4 = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    return (
        <div className='tutorial-container align-items-center justify-content-center d-flex flex-column ' style={{ height: "100%" }}>
            <div>
                <h1 className="fw-bold">{t(`PopUpPage4Title`)} ! </h1>
                <div style={{ flex: "1 1 auto", display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                </div>
                <div className="d-flex justify-content-center mt-5" style={{ fontSize: "1.1rem" }}>
                    <ol className='text-start d-none d-lg-flex flex-column m-0'>
                        <li>{t("PopUpPage4Point2")}</li>
                        <li>{t("PopUpPage4Point4")}</li>
                    </ol>
                </div>
                <button className='btn-Custom btn-IDGreen d-inline text-white mt-5' style={{ fontSize: "1.1rem", padding: "0.6rem 1.4rem" }} type='button' onClick={() => navigate("/signup")}>
                    {t(`PopUpPage4Point1`)}
                </button>
            </div>  
        </div>
    )
}

export default Tutorial
